import { type INode, parse, stringify } from 'svgson';
import svgpath from 'svgpath';
import { ImageUtil } from './util.image';
import { CUTLINER_OPTS } from '../Constant/stickerizer.constant';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';

/**
 * Type for SVG path style properties.
 */
export type SvgPathStyle = {
  fill?: string;
  stroke?: string;
  strokeWidth?: string;
  strokeDasharray?: string;
  [key: string]: string | undefined;
};

type Size = {
  width: number;
  height: number;
};

/**
 * SVG 관련 유틸리티 함수들을 static 메서드로 제공하는 클래스
 */
export class SvgUtil {
  /**
   * HEX 색상 문자열을 [R, G, B] 배열로 변환합니다. (예: #d22486 → [210, 36, 134])
   * @param hex - HEX 색상 문자열
   * @returns [number, number, number] | null
   */
  static hexToRgb(hex: string): [number, number, number] | null {
    let hexString = hex.trim();
    if (hexString.startsWith('#')) hexString = hexString.slice(1);
    if (hexString.length === 3) {
      hexString = hexString
        .split('')
        .map((c) => c + c)
        .join('');
    }
    if (hexString.length !== 6) return null;
    const num = parseInt(hexString, 16);
    if (isNaN(num)) return null;
    return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
  }

  /**
   * 지정한 RGB 정수 기준으로, 퍼센트 기반 SVG 색상 문자열이 일치하는지 검사합니다.
   * @param colorString - 예: "rgb(82.398987%, 14.099121%, 52.49939%)" 또는 "#d22486"
   * @param targetRgb - 정수 RGB 값 [R, G, B], 예: [210, 36, 134]
   * @param tolerance - 허용 오차 (0~255 기준), 기본값 1
   * @returns boolean - 일치 여부
   */
  static isMatchingRgb(colorString: string, targetRgb: [number, number, number], tolerance = 1): boolean {
    // HEX 코드인 경우
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(colorString.trim())) {
      const parsedHex = SvgUtil.hexToRgb(colorString);
      if (!parsedHex) return false;
      return parsedHex.every((val, i) => {
        const targetValue = targetRgb[i];
        if (targetValue === undefined) {
          throw new Error(`Invalid target RGB value at index ${i}`);
        }
        return Math.abs(val - targetValue) <= tolerance;
      });
    }
    // rgb(%,%,%) 형태인 경우
    const match = colorString.match(/rgb\(([-\d.]+)%\s*,\s*([-\d.]+)%\s*,\s*([-\d.]+)%\s*\)/i);
    if (!match) return false;
    const [_, r, g, b] = match as [string, string, string, string];
    const parsedRgb: [number, number, number] = [
      Math.round((parseFloat(r) / 100) * 255),
      Math.round((parseFloat(g) / 100) * 255),
      Math.round((parseFloat(b) / 100) * 255),
    ];
    return parsedRgb.every((val, i) => {
      const targetValue = targetRgb[i];
      if (targetValue === undefined) {
        throw new Error(`Invalid target RGB value at index ${i}`);
      }
      return Math.abs(val - targetValue) <= tolerance;
    });
  }

  /**
   * Replace <path> styles using svgson: removes class/id/style, applies given style as direct attributes.
   * @param svgString - The SVG string to modify
   * @param style - SvgPathStyle object
   * @returns Promise<string> - Modified SVG string
   */
  static async replacePathStyles(svgString: string, style: SvgPathStyle): Promise<string>;
  /**
   * Replace <path> styles using svgson: removes class/id/style, applies given style as direct attributes.
   * @param svgJson - The SVG INode to modify
   * @param style - SvgPathStyle object
   * @returns Promise<INode> - Modified SVG INode
   */
  static async replacePathStyles(svgJson: INode, style: SvgPathStyle): Promise<INode>;
  static async replacePathStyles(
    svgStringOrJson: string | INode,
    style: SvgPathStyle,
  ): Promise<string | INode> {
    let svgJson: INode;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
    } else {
      svgJson = svgStringOrJson;
    }

    function applyStylesToPath(node: INode): INode {
      if (node.name === 'path') {
        delete node.attributes.class;
        delete node.attributes.id;
        delete node.attributes.style;
        Object.entries(style).forEach(([key, val]) => {
          if (val !== undefined) {
            node.attributes[key] = val;
          }
        });
      }
      if (node.children && node.children.length) {
        node.children = node.children.map(applyStylesToPath);
      }
      return node;
    }
    const updatedJson = applyStylesToPath(svgJson);

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(updatedJson);
    } else {
      return updatedJson;
    }
  }

  /**
   * SVG 문자열에 대해 scale factor를 적용해 width/height, viewBox, 각 요소의 좌표를 재계산합니다.
   * @param svgString - SVG 문자열
   * @param scale - 스케일 팩터 (예: 2)
   * @param precision - 좌표 계산 시 소수점 정확도 (기본값: 3)
   * @returns Promise<string> - 스케일이 적용된 SVG 문자열
   */
  static async scaleSvgByRecalculating(svgString: string, scale: number, precision = 3): Promise<string> {
    const svgJson = await parse(svgString);

    // 정확도에 따른 반올림 헬퍼 함수
    const roundToPrecision = (value: number): number => {
      return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
    };

    const scaleNumericAttr = (attrs: Record<string, string>, keys: string[]) => {
      for (const key of keys) {
        if (attrs[key] !== undefined) {
          const n = parseFloat(attrs[key]);
          if (!isNaN(n)) {
            attrs[key] = roundToPrecision(n * scale).toString();
          }
        }
      }
    };

    const transformNode = (node: INode): INode => {
      const name = node.name;
      const attrs = node.attributes || {};

      switch (name) {
        case 'path':
          if (attrs.d) {
            const scaledPath = svgpath(attrs.d).scale(scale).toString();
            // path 데이터의 모든 숫자에 precision 적용
            attrs.d = scaledPath.replace(/([-+]?\d*\.?\d+)/g, (match) => {
              const num = parseFloat(match);
              if (!isNaN(num)) {
                return roundToPrecision(num).toString();
              }
              return match;
            });
          }
          break;
        case 'rect':
          scaleNumericAttr(attrs, ['x', 'y', 'width', 'height', 'rx', 'ry']);
          break;
        case 'circle':
          scaleNumericAttr(attrs, ['cx', 'cy', 'r']);
          break;
        case 'ellipse':
          scaleNumericAttr(attrs, ['cx', 'cy', 'rx', 'ry']);
          break;
        case 'line':
          scaleNumericAttr(attrs, ['x1', 'y1', 'x2', 'y2']);
          break;
        case 'polyline':
        case 'polygon':
          if (attrs.points) {
            attrs.points = attrs.points
              .trim()
              .split(/\s+/)
              .map((pt) =>
                pt
                  .split(',')
                  .map((n) => roundToPrecision(parseFloat(n) * scale).toString())
                  .join(','),
              )
              .join(' ');
          }
          break;
        case 'text':
          scaleNumericAttr(attrs, ['x', 'y', 'font-size']);
          break;
      }

      const scaledChildren = node.children?.map(transformNode) ?? [];

      return { ...node, attributes: attrs, children: scaledChildren };
    };

    const newSvg = { ...svgJson };

    // width/height scale
    const w = parseFloat(newSvg.attributes.width || '0');
    const h = parseFloat(newSvg.attributes.height || '0');
    if (w && h) {
      newSvg.attributes.width = roundToPrecision(w * scale).toString();
      newSvg.attributes.height = roundToPrecision(h * scale).toString();
    }

    // viewBox scale (있는 경우)
    if (newSvg.attributes.viewBox) {
      // 공백 또는 콤마로 구분된 값 모두 지원
      const viewBoxParts = newSvg.attributes.viewBox.split(/[ ,]+/).map(Number);
      if (viewBoxParts.length === 4 && viewBoxParts.every((v) => !isNaN(v))) {
        const [x, y, vw, vh] = viewBoxParts as [number, number, number, number];
        newSvg.attributes.viewBox = [
          roundToPrecision(x * scale),
          roundToPrecision(y * scale),
          roundToPrecision(vw * scale),
          roundToPrecision(vh * scale),
        ].join(' ');
      }
    } else if (w && h) {
      // viewBox가 없는 경우에만 width, height로 viewBox 생성
      newSvg.attributes.viewBox = `0 0 ${w} ${h}`;
    }

    newSvg.children = newSvg.children?.map(transformNode) ?? [];

    return stringify(newSvg);
  }

  /**
   * SVG 문자열의 <svg> 태그 내 width/height를 새로운 값으로 치환합니다.
   * @param svgString - 원본 SVG 문자열
   * @param width - 새 width(px)
   * @param height - 새 height(px)
   * @returns string - width/height가 치환된 SVG 문자열
   */
  static replaceSvgSize(svgString: string, width: number, height: number): string {
    const svgTagMatch = svgString.match(/<svg[^>]*>/i);
    if (!svgTagMatch) throw new Error('No <svg> tag found');
    const svgTag = svgTagMatch[0];
    const newSvgTag = svgTag
      .replace(/width\s*=\s*['"][\d.]+['"]/i, `width="${width}"`)
      .replace(/height\s*=\s*['"][\d.]+['"]/i, `height="${height}"`);
    return svgString.replace(svgTag, newSvgTag);
  }

  /**
   * SVG 문자열에 대해 scale factor 또는 size를 받아 viewBox/width/height만 조정하여 스케일링합니다.
   * @param svgString - SVG 문자열
   * @param options - { scale?: number, size?: { width: number, height: number } }
   * @returns Promise<string> - 스케일이 적용된 SVG 문자열
   */
  static sizeScaleSvg(
    svgString: string,
    options: { scale?: number; size?: { width: number; height: number } },
  ): string {
    const { width: origWidth, height: origHeight } = this.extractSvgSize(svgString);
    let scale: number;
    if (typeof options.scale === 'number') {
      scale = options.scale;
    } else if (options.size) {
      const { width, height } = options.size;
      scale = Math.min(width / origWidth, height / origHeight);
    } else {
      throw new Error('Either scale or size must be provided');
    }
    const newWidth = +(origWidth * scale).toFixed(2);
    const newHeight = +(origHeight * scale).toFixed(2);
    return this.replaceSvgSize(svgString, newWidth, newHeight);
  }

  /*
  1. 추가 메서드
     - static async putImageToSvgWithLink(svgString: string, imageUrl: string, padding: number): Promise<string>
       - embed 대신 href로 URL 참조하는 방식 (브라우저 환경, xlink:href 제거)
  2. 추가 메서드
    - static async convertImageLinkToEmbedInSvg(svgString: string): Promise<string>
      - svg 요소 중 href로 되어 있는 image 요소를 찾아서 참조하는 이미지를 다운로드 하고 base64 dataUri 로 변환하여 embed 형태로 전환
  */

  /**
   * Extract SVG dimensions and viewBox information
   * @param svgString - SVG string to analyze
   * @returns Object with SVG dimensions and viewBox data
   */
  private static extractSvgInfo(svgString: string): {
    svgTag: string;
    svgWidth: number;
    svgHeight: number;
    viewBox: { x: number; y: number; width: number; height: number };
    scaleX: number;
    scaleY: number;
  } {
    const svgTagMatch = svgString.match(/<svg[^>]*>/i);
    if (!svgTagMatch) throw new Error('No <svg> tag found');
    const svgTag = svgTagMatch[0];

    const widthMatch = svgTag.match(/width\s*=\s*['"]([\d.]+)['"]/i);
    const heightMatch = svgTag.match(/height\s*=\s*['"]([\d.]+)['"]/i);
    if (!widthMatch || !widthMatch[1] || !heightMatch || !heightMatch[1])
      throw new Error('SVG must have width and height attributes');

    const svgWidth = parseFloat(widthMatch[1]);
    const svgHeight = parseFloat(heightMatch[1]);
    if (!svgWidth || !svgHeight) throw new Error('Invalid SVG width/height');

    // viewBox 추출
    const viewBoxMatch = svgTag.match(/viewBox\s*=\s*['"]([\d.\s-]+)['"]/i);
    let vbX = 0;
    let vbY = 0;
    let vbW = svgWidth;
    let vbH = svgHeight;
    if (viewBoxMatch && viewBoxMatch[1]) {
      const parts = viewBoxMatch[1].split(/\s+/).map(Number);
      if (parts.length === 4 && parts.every((v) => !isNaN(v))) {
        vbX = parts[0] ?? 0;
        vbY = parts[1] ?? 0;
        vbW = parts[2] ?? svgWidth;
        vbH = parts[3] ?? svgHeight;
      }
    }

    // scale 계산 (svg px : viewBox 단위)
    const scaleX = svgWidth / vbW;
    const scaleY = svgHeight / vbH;

    return {
      svgTag,
      svgWidth,
      svgHeight,
      viewBox: { x: vbX, y: vbY, width: vbW, height: vbH },
      scaleX,
      scaleY,
    };
  }

  /**
   * Calculate image position and size within SVG based on padding
   * @param svgInfo - SVG information from extractSvgInfo
   * @param padding - Padding in pixels
   * @returns Image dimensions and position in viewBox coordinates
   */
  private static calculateImageDimensions(
    svgInfo: {
      viewBox: { x: number; y: number; width: number; height: number };
      scaleX: number;
      scaleY: number;
    },
    padding: number,
  ): { x: number; y: number; width: number; height: number } {
    const { viewBox, scaleX, scaleY } = svgInfo;

    // padding을 viewBox 단위로 환산
    const vbPaddingX = padding / scaleX;
    const vbPaddingY = padding / scaleY;

    // 이미지 위치/크기 계산 (viewBox 기준)
    const imgX = viewBox.x + vbPaddingX;
    const imgY = viewBox.y + vbPaddingY;
    const imgWidth = viewBox.width - vbPaddingX * 2;
    const imgHeight = viewBox.height - vbPaddingY * 2;

    if (imgWidth <= 0 || imgHeight <= 0) throw new Error('Padding too large for SVG size');

    return { x: imgX, y: imgY, width: imgWidth, height: imgHeight };
  }

  /**
   * URL을 base64 data URI로 변환합니다.
   * @param url - 변환할 이미지 URL
   * @returns Promise<string> - base64 data URI
   */
  private static async urlToDataUri(url: string): Promise<string> {
    return ImageUtil.urlToDataUri(url);
  }

  /**
   * Insert image elements into SVG string
   * @param svgString - Original SVG string
   * @param svgTag - Original SVG tag
   * @param defsTag - Defs element containing image definition
   * @param useTag - Use element with href
   * @returns SVG string with image elements inserted
   */
  private static insertImageElements(
    svgString: string,
    svgTag: string,
    defsTag: string,
    useTag: string,
  ): string {
    const svgOpenTagEnd = svgString.indexOf('>');
    if (svgOpenTagEnd === -1) throw new Error('Malformed SVG');
    const isSelfClosing = svgTag.endsWith('/>');
    if (isSelfClosing) throw new Error('Self-closing <svg/> not supported');

    // SVG 닫는 태그 위치 찾기
    const closingTagIndex = svgString.lastIndexOf('</svg>');
    if (closingTagIndex === -1) throw new Error('No closing </svg> tag found');

    // 이미지 요소를 SVG 닫는 태그 직전에 삽입
    const before = svgString.slice(0, closingTagIndex);
    const after = svgString.slice(closingTagIndex);

    return `${before}${defsTag}${useTag}${after}`;
  }

  /**
   * SVG 문자열에 이미지를 URL 링크로 중앙에 padding만큼 줄여서 추가합니다.
   * @param svgString - 원본 SVG 문자열
   * @param imageUrl - 삽입할 이미지의 URL (URL 그대로 참조)
   * @param padding - 이미지와 SVG 경계 사이의 padding(px)
   * @returns Promise<string> - 이미지가 추가된 SVG 문자열
   */
  static async putImageToSvgWithLink(svgString: string, imageUrl: string, padding: number): Promise<string> {
    // 1. SVG 정보 추출
    const svgInfo = this.extractSvgInfo(svgString);

    // 2. 이미지 위치/크기 계산 (viewBox 기준)
    const imageDimensions = this.calculateImageDimensions(svgInfo, padding);

    // 3. <defs><image id="img1" ... /></defs> 및 <use> 태그 생성 (viewBox 기준 좌표)
    const imageId = 'design-image-svg';
    const defsTag = `<defs><image id="${imageId}" x="${imageDimensions.x}" y="${imageDimensions.y}" width="${imageDimensions.width}" height="${imageDimensions.height}" href="${imageUrl}"/></defs>`;
    const useTag = `<use href="#${imageId}" />`;

    // 4. SVG에 이미지 요소들 삽입
    return this.insertImageElements(svgString, svgInfo.svgTag, defsTag, useTag);
  }

  /**
   * SVG 문자열에 이미지를 중앙에 padding만큼 줄여서 추가합니다.
   * @param svgString - 원본 SVG 문자열
   * @param imageUrl - 삽입할 이미지의 URL (base64 datauri로 변환됨)
   * @param padding - 이미지와 SVG 경계 사이의 padding(px)
   * @returns Promise<string> - 이미지가 추가된 SVG 문자열
   */
  static async putImageToSvgWithEmbed(svgString: string, imageUrl: string, padding: number): Promise<string> {
    // 1. SVG 정보 추출
    const svgInfo = this.extractSvgInfo(svgString);

    // 2. 이미지 위치/크기 계산 (viewBox 기준)
    const imageDimensions = this.calculateImageDimensions(svgInfo, padding);

    // 3. 이미지 base64 datauri 변환
    const dataUri = await this.urlToDataUri(imageUrl);

    // 4. <defs><image id="img1" ... /></defs> 및 <use> 태그 생성 (viewBox 기준 좌표)
    const imageId = 'design-image-svg';
    const defsTag = `<defs><image id="${imageId}" x="${imageDimensions.x}" y="${imageDimensions.y}" width="${imageDimensions.width}" height="${imageDimensions.height}" href="${dataUri}"/></defs>`;
    const useTag = `<use href="#${imageId}" />`;

    // 5. SVG에 이미지 요소들 삽입
    return this.insertImageElements(svgString, svgInfo.svgTag, defsTag, useTag);
  }

  /**
   * SVG 문자열의 width/height(px, 96dpi 기준)를 300dpi 기준 px로 변환하여 반환합니다.
   * @param svgString - 원본 SVG 문자열 (px 단위 width/height, 96dpi 기준)
   * @returns string - 300dpi 기준 px로 변환된 SVG 문자열
   */
  static to300dpi(svgString: string): string {
    return this.sizeScaleSvg(svgString, { scale: CUTLINER_OPTS.WEB_TO_DPI_300_RATIO });
  }

  /**
   * 현재 실행 환경이 브라우저(window+document)인지 여부를 반환
   */
  private static isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.document !== 'undefined';
  }

  /**
   * SVG 문자열을 drawImage로 캔버스에 rasterize합니다.
   * @param svgString - SVG 소스 문자열
   * @returns Promise<HTMLCanvasElement>
   */
  static async toRasterize(svgString: string): Promise<HTMLCanvasElement> {
    let svgUrl: string;
    if (this.isBrowser() && 'createImageBitmap' in window) {
      svgUrl = this.getSvgBlobUrl(svgString);
    } else {
      svgUrl = this.getSvgDataUri(svgString);
    }

    return (await ImageUtil.makeCanvasByUrl(svgUrl)) as HTMLCanvasElement;
  }

  static extractSvgSize(svgString: string): { width: number; height: number } {
    const svgTag = svgString.match(/<svg[^>]*>/i)?.[0];
    if (!svgTag) throw new Error('No <svg> tag found');
    const width = parseFloat(svgTag.match(/width\s*=\s*['"]([\d.]+)['"]/i)?.[1] ?? '');
    const height = parseFloat(svgTag.match(/height\s*=\s*['"]([\d.]+)['"]/i)?.[1] ?? '');
    if (!width || !height) throw new Error('SVG must have width and height attributes');
    return { width, height };
  }

  static getSvgBlobUrl(svgString: string): string {
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
  }

  // Node.js에서 사용할 data URI 생성 함수
  static getSvgDataUri(svgString: string): string {
    return 'data:image/svg+xml;base64,' + Buffer.from(svgString).toString('base64');
  }

  /**
   * SVG 문자열에서 <image> 요소만 분리하여 두 개의 SVG 문자열로 반환합니다.
   * - svgHavingImage: 각 image 요소가 원래 렌더링된 컨텍스트(부모 chain)를 모두 유지, image 외 다른 sibling은 제거
   * - svgExcludeImage: 모든 image 요소를 재귀적으로 제거
   * @param svgString - 원본 SVG 문자열
   * @returns Promise<{ svgHavingImage: string, svgExcludeImage: string }>
   */
  static async splitImageFromSvg(
    svgString: string,
  ): Promise<{ svgHavingImage: string; svgExcludeImage: string }> {
    const svgJson = await parse(svgString);

    if (!Array.isArray(svgJson.children)) throw new Error('Invalid SVG: children must be array');

    // 1. <defs> 내 <image> id 수집
    const imageIdSet = new Set<string>();
    function collectImageIds(node: INode) {
      if (node.name === 'defs' && Array.isArray(node.children)) {
        for (const child of node.children) {
          if (child.name === 'image' && child.attributes?.id) {
            imageIdSet.add(`#${child.attributes.id}`);
          }
        }
      }
      if (node.children) node.children.forEach(collectImageIds);
    }
    collectImageIds(svgJson);

    // 3. removeImageRecursively: <image>와 <use>가 <image>를 참조하면 모두 제거
    function removeImageRecursively(node: INode): INode | null {
      if (node.name === 'image') return null;
      if (node.name === 'use') {
        const href = node.attributes?.href;
        if (href && imageIdSet.has(href)) {
          return null;
        }
      }
      if (!node.children || node.children.length === 0) return node;
      const filteredChildren = node.children.map(removeImageRecursively).filter(Boolean) as INode[];
      return { ...node, children: filteredChildren };
    }

    // 2. extractImageSubtrees: <image> 또는 <use>가 <image>를 참조하면 부모 chain 유지
    function extractImageSubtrees(node: INode): INode | null {
      // <image> 직접
      if (node.name === 'image') return { ...node };
      // <use>가 <image>를 참조하는 경우
      if (node.name === 'use') {
        const href = node.attributes?.href;
        if (href && imageIdSet.has(href)) {
          return { ...node };
        }
        return null;
      }
      if (!node.children || node.children.length === 0) return null;
      const imageChildren = node.children.map(extractImageSubtrees).filter(Boolean) as INode[];
      if (imageChildren.length === 0) return null;
      return { ...node, children: imageChildren };
    }

    // svgHavingImage: <image> 또는 <use>가 <image>를 참조하는 것만 남기고 부모 chain 유지
    const imageTree = extractImageSubtrees(svgJson);
    const svgHavingImage = stringify(imageTree || { ...svgJson, children: [] });

    // svgExcludeImage: <image>와 <use>가 <image>를 참조하는 것 모두 제거
    const excludeTree = removeImageRecursively(svgJson);
    const svgExcludeImage = stringify(excludeTree || { ...svgJson, children: [] });
    return { svgHavingImage, svgExcludeImage };
  }

  /**
   * SVG의 viewBox를 0 0 width height로 정규화합니다.
   * 기존 viewBox가 있을 때는 translate와 scale을 계산하여 모든 path 요소의 좌표를 재계산하고,
   * 렌더링 결과는 동일하게 유지하면서 viewBox만 정규화합니다.
   * @param svgString - 원본 SVG 문자열
   * @param precision - 좌표 계산 시 소수점 정확도 (기본값: 4)
   * @param scale - 추가 스케일 팩터 (기본값: 1)
   * @returns Promise<string> - viewBox가 정규화된 SVG 문자열
   */
  static async normalizeViewBoxWithScale(svgString: string, precision = 4, scale = 1): Promise<string> {
    // 1. SVG의 width/height 추출
    const { width, height } = this.extractSvgSize(svgString);

    // 2. SVG JSON 파싱
    const svgJson = await parse(svgString);

    // 3. 현재 viewBox 분석
    const currentViewBox = svgJson.attributes?.viewBox;
    let translateX = 0;
    let translateY = 0;
    let scaleX = 1;
    let scaleY = 1;

    if (currentViewBox && typeof currentViewBox === 'string') {
      const viewBoxParts = currentViewBox.split(/\s+/).map(Number);
      if (viewBoxParts.length === 4 && viewBoxParts.every((v) => !isNaN(v))) {
        const [vbX, vbY, vbWidth, vbHeight] = viewBoxParts;
        if (vbX !== undefined && vbY !== undefined && vbWidth !== undefined && vbHeight !== undefined) {
          // viewBox에서 실제 좌표계로 변환하기 위한 변환 계산
          // 현재 viewBox (vbX, vbY, vbWidth, vbHeight)를 (0, 0, width, height)로 변환
          // 추가 스케일 팩터도 함께 적용
          scaleX = (width / vbWidth) * scale;
          scaleY = (height / vbHeight) * scale;
          translateX = -vbX * scaleX;
          translateY = -vbY * scaleY;
        }
      }
    } else {
      // viewBox가 없는 경우에도 scale 팩터만 적용
      scaleX = scale;
      scaleY = scale;
    }

    // 4. 모든 요소의 좌표를 재계산하는 함수
    const transformNode = (node: INode): INode => {
      const name = node.name;
      const attrs = { ...(node.attributes || {}) };

      // 정확도에 따른 반올림 헬퍼 함수
      const roundToPrecision = (value: number): number => {
        return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
      };

      // 숫자 속성을 변환하는 헬퍼 함수
      const transformCoordinate = (value: string | undefined, isX: boolean): string | undefined => {
        if (!value) return value;
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        const scale = isX ? scaleX : scaleY;
        const translate = isX ? translateX : translateY;
        const transformed = roundToPrecision(num * scale + translate);
        return transformed.toString();
      };

      const transformNumericAttr = (
        attrs: Record<string, string>,
        coordKeys: { key: string; isX: boolean }[],
      ) => {
        for (const { key, isX } of coordKeys) {
          if (attrs[key] !== undefined) {
            const transformed = transformCoordinate(attrs[key], isX);
            if (transformed !== undefined) {
              attrs[key] = transformed;
            }
          }
        }
      };

      switch (name) {
        case 'path':
          if (attrs.d && typeof attrs.d === 'string') {
            // viewBox 변환을 올바르게 적용하기 위해 matrix 변환 사용
            // 변환 공식: newCoord = (oldCoord - offset) * scale
            // 이는 translate(-offset) -> scale(scale) 순서와 동일
            let transformedPath;

            if (currentViewBox && typeof currentViewBox === 'string') {
              const viewBoxParts = currentViewBox.split(/\s+/).map(Number);
              if (viewBoxParts.length === 4 && viewBoxParts.every((v) => !isNaN(v))) {
                const vbX = viewBoxParts[0]!;
                const vbY = viewBoxParts[1]!;
                const vbWidth = viewBoxParts[2]!;
                const vbHeight = viewBoxParts[3]!;

                // viewBox 변환: 먼저 viewBox 원점으로 이동 -> 스케일 적용
                const finalScaleX = (width / vbWidth) * scale;
                const finalScaleY = (height / vbHeight) * scale;
                transformedPath = svgpath(attrs.d).translate(-vbX, -vbY).scale(finalScaleX, finalScaleY);
              } else {
                // viewBox 파싱 실패 시 기본 스케일만 적용
                transformedPath = svgpath(attrs.d).scale(scaleX, scaleY);
              }
            } else {
              // viewBox가 없는 경우 스케일만 적용
              transformedPath = svgpath(attrs.d).scale(scaleX, scaleY);
            }

            // 변환된 path 데이터를 문자열로 변환하고 precision 적용
            let pathData = transformedPath.toString();

            // 다른 요소들과 동일한 precision 적용
            pathData = pathData.replace(/([-+]?\d*\.?\d+)/g, (match) => {
              const num = parseFloat(match);
              if (!isNaN(num)) {
                return roundToPrecision(num).toString();
              }
              return match;
            });

            attrs.d = pathData;
          }
          break;
        case 'rect':
          // 위치 좌표 (x, y)에는 scale과 translate 모두 적용
          transformNumericAttr(attrs, [
            { key: 'x', isX: true },
            { key: 'y', isX: false },
          ]);
          // 크기 (width, height)와 radius (rx, ry)에는 scale만 적용 (translate 제외)
          if (attrs.width) {
            const width = parseFloat(attrs.width);
            if (!isNaN(width)) {
              const scaledWidth = roundToPrecision(width * scaleX);
              attrs.width = scaledWidth.toString();
            }
          }
          if (attrs.height) {
            const height = parseFloat(attrs.height);
            if (!isNaN(height)) {
              const scaledHeight = roundToPrecision(height * scaleY);
              attrs.height = scaledHeight.toString();
            }
          }
          if (attrs.rx) {
            const rx = parseFloat(attrs.rx);
            if (!isNaN(rx)) {
              const scaledRx = roundToPrecision(rx * scaleX);
              attrs.rx = scaledRx.toString();
            }
          }
          if (attrs.ry) {
            const ry = parseFloat(attrs.ry);
            if (!isNaN(ry)) {
              const scaledRy = roundToPrecision(ry * scaleY);
              attrs.ry = scaledRy.toString();
            }
          }
          break;
        case 'circle':
          transformNumericAttr(attrs, [
            { key: 'cx', isX: true },
            { key: 'cy', isX: false },
          ]);
          // 반지름은 스케일의 평균값 적용
          if (attrs.r) {
            const r = parseFloat(attrs.r);
            if (!isNaN(r)) {
              const scaledR = roundToPrecision(r * Math.sqrt(scaleX * scaleY));
              attrs.r = scaledR.toString();
            }
          }
          break;
        case 'ellipse':
          // 위치 좌표 (cx, cy)에는 scale과 translate 모두 적용
          transformNumericAttr(attrs, [
            { key: 'cx', isX: true },
            { key: 'cy', isX: false },
          ]);
          // 반지름 (rx, ry)에는 scale만 적용 (translate 제외)
          if (attrs.rx) {
            const rx = parseFloat(attrs.rx);
            if (!isNaN(rx)) {
              const scaledRx = roundToPrecision(rx * scaleX);
              attrs.rx = scaledRx.toString();
            }
          }
          if (attrs.ry) {
            const ry = parseFloat(attrs.ry);
            if (!isNaN(ry)) {
              const scaledRy = roundToPrecision(ry * scaleY);
              attrs.ry = scaledRy.toString();
            }
          }
          break;
        case 'line':
          transformNumericAttr(attrs, [
            { key: 'x1', isX: true },
            { key: 'y1', isX: false },
            { key: 'x2', isX: true },
            { key: 'y2', isX: false },
          ]);
          break;
        case 'polyline':
        case 'polygon':
          if (attrs.points) {
            attrs.points = attrs.points
              .trim()
              .split(/\s+/)
              .map((pt) => {
                const coords = pt.split(',');
                if (coords.length === 2) {
                  const x = parseFloat(coords[0]!);
                  const y = parseFloat(coords[1]!);
                  if (!isNaN(x) && !isNaN(y)) {
                    const newX = roundToPrecision(x * scaleX + translateX);
                    const newY = roundToPrecision(y * scaleY + translateY);
                    return `${newX},${newY}`;
                  }
                }
                return pt;
              })
              .join(' ');
          }
          break;
        case 'text':
          transformNumericAttr(attrs, [
            { key: 'x', isX: true },
            { key: 'y', isX: false },
          ]);
          // font-size는 스케일 평균값 적용
          if (attrs['font-size']) {
            const fontSize = parseFloat(attrs['font-size']);
            if (!isNaN(fontSize)) {
              const scaledFontSize = roundToPrecision(fontSize * Math.sqrt(scaleX * scaleY));
              attrs['font-size'] = scaledFontSize.toString();
            }
          }
          break;
        case 'image':
          // 이미지의 위치 좌표 (x, y)에는 scale과 translate 모두 적용
          transformNumericAttr(attrs, [
            { key: 'x', isX: true },
            { key: 'y', isX: false },
          ]);
          // 이미지의 크기 (width, height)에는 scale만 적용 (translate 제외)
          if (attrs.width) {
            const width = parseFloat(attrs.width);
            if (!isNaN(width)) {
              const scaledWidth = roundToPrecision(width * scaleX);
              attrs.width = scaledWidth.toString();
            }
          }
          if (attrs.height) {
            const height = parseFloat(attrs.height);
            if (!isNaN(height)) {
              const scaledHeight = roundToPrecision(height * scaleY);
              attrs.height = scaledHeight.toString();
            }
          }
          break;
      }

      // transform 속성이 있다면 추가 변환 적용
      if (attrs.transform) {
        // 기존 transform에 새로운 변환을 앞에 추가
        const roundedScaleX = roundToPrecision(scaleX);
        const roundedScaleY = roundToPrecision(scaleY);
        const roundedTranslateX = roundToPrecision(translateX);
        const roundedTranslateY = roundToPrecision(translateY);
        const newTransform = `matrix(${roundedScaleX},0,0,${roundedScaleY},${roundedTranslateX},${roundedTranslateY})`;
        attrs.transform = `${newTransform} ${attrs.transform}`;
      } else if (scaleX !== 1 || scaleY !== 1 || translateX !== 0 || translateY !== 0) {
        // transform이 없고 변환이 필요한 경우에만 추가
        // 하지만 이미 개별 좌표를 변환했으므로 일반적으로는 불필요
      }

      // 자식 노드들도 재귀적으로 변환
      const transformedChildren = node.children?.map(transformNode) ?? [];

      return { ...node, attributes: attrs, children: transformedChildren };
    };

    // 5. 모든 노드 변환
    const transformedSvg = transformNode(svgJson);

    // 6. viewBox를 0 0 width height로 설정 (scale 고려)
    const finalWidth = Math.round(width * scale * Math.pow(10, precision)) / Math.pow(10, precision);
    const finalHeight = Math.round(height * scale * Math.pow(10, precision)) / Math.pow(10, precision);
    transformedSvg.attributes = {
      ...transformedSvg.attributes,
      width: finalWidth.toString(),
      height: finalHeight.toString(),
      viewBox: `0 0 ${finalWidth} ${finalHeight}`,
    };

    // 7. 변환된 SVG 문자열 반환
    let resultSvgString = stringify(transformedSvg);

    // 8. SVG 문자열의 모든 숫자 속성에 precision 적용 (stringify 후처리)
    // precision에 따른 반올림 헬퍼 함수 (외부 스코프용)
    const roundToPrecision = (value: number): number => {
      return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
    };

    // 속성값의 숫자들에 precision 적용
    resultSvgString = resultSvgString.replace(
      /(\w+)="([-+]?\d*\.?\d+([eE][-+]?\d+)?)"/g,
      (match, attrName, numStr) => {
        // viewBox는 이미 정확히 설정했으므로 제외
        if (attrName === 'viewBox') return match;

        const num = parseFloat(numStr);
        if (!isNaN(num)) {
          const rounded = roundToPrecision(num);
          return `${attrName}="${rounded}"`;
        }
        return match;
      },
    );

    // 좌표 리스트 (points, d 등)의 숫자들에도 precision 적용
    resultSvgString = resultSvgString.replace(/(points|d)="([^"]+)"/g, (match, attrName, value) => {
      if (attrName === 'viewBox') return match;

      const processedValue = value.replace(/([-+]?\d*\.?\d+([eE][-+]?\d+)?)/g, (numMatch: string) => {
        const num = parseFloat(numMatch);
        if (!isNaN(num)) {
          return roundToPrecision(num).toString();
        }
        return numMatch;
      });

      return `${attrName}="${processedValue}"`;
    });

    return resultSvgString;
  }

  static getSvgDimension(svgString: string, dpi = 300): { px: Size; mm: Size; ratio: number } {
    const precision = 3;
    const { width: width_px, height: height_px } = this.extractSvgSize(svgString);
    const width_mm = Math.round((width_px / dpi) * 25.4 * Math.pow(10, precision)) / Math.pow(10, precision);
    const height_mm =
      Math.round((height_px / dpi) * 25.4 * Math.pow(10, precision)) / Math.pow(10, precision);
    return {
      px: { width: width_px, height: height_px },
      mm: { width: width_mm, height: height_mm },
      ratio: Math.round((width_px / height_px) * Math.pow(10, precision)) / Math.pow(10, precision),
    };
  }

  static rotateSvgPathdata(pathData: string, origin?: { x: number; y: number }): string {
    let path = svgpath(pathData);

    if (origin) {
      // SVG 중심 기준 회전: 이론대로 정확함!
      path = path
        .translate(-origin.x, -origin.y) // 중심을 원점으로
        .rotate(-90) // 원점 기준 -90도 회전
        .translate(origin.x, origin.y); // 다시 원래 중심으로
    } else {
      path = path.rotate(-90);
    }

    return path.toString();
  }

  /**
   * Remove bounding box rectangle from SVG string
   */
  static async removeBoundingBoxRectangle(svgString: string): Promise<string>;
  /**
   * Remove bounding box rectangle from SVG INode
   */
  static async removeBoundingBoxRectangle(svgJson: INode, width: number, height: number): Promise<INode>;
  static async removeBoundingBoxRectangle(
    svgStringOrJson: string | INode,
    width?: number,
    height?: number,
  ): Promise<string | INode> {
    let svgJson: INode;
    let svgWidth: number;
    let svgHeight: number;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
      const size = this.extractSvgSize(svgStringOrJson);
      svgWidth = size.width;
      svgHeight = size.height;
    } else {
      svgJson = svgStringOrJson;
      if (width === undefined || height === undefined) {
        throw new Error('Width and height must be provided when using INode input');
      }
      svgWidth = width;
      svgHeight = height;
    }

    // Tolerance for size comparison (5% of the dimension)
    const tolerance = Math.max(svgWidth, svgHeight) * 0.05;

    /**
     * Remove bounding box segments from compound path:
     * 1. Split compound path by Z/z closepath commands
     * 2. For each segment, check if it's a bounding box (starts with M/m 0,0 and contains SVG dimensions)
     * 3. Remove only the bounding box segments, keep other segments
     */
    const removeBoundingBoxFromCompoundPath = (pathData: string): string => {
      if (!pathData) return pathData;

      const trimmedPath = pathData.trim();

      // Check if this is a compound path (contains multiple Z/z commands)
      const zMatches = trimmedPath.match(/[Zz]/g);
      if (!zMatches || zMatches.length <= 1) {
        // Single path - check if it's a bounding box
        if (isBoundingBoxSegment(trimmedPath)) {
          return ''; // Remove the entire path if it's a bounding box
        }
        return pathData; // Keep the path if it's not a bounding box
      }

      // Split compound path into segments by Z/z commands
      const segments = trimmedPath.split(/([Zz])/i).reduce((acc, part, index, array) => {
        if (index % 2 === 0 && index < array.length - 1) {
          // Combine path segment with its closing Z/z
          const segment = part + (array[index + 1] || '');
          acc.push(segment);
        }
        return acc;
      }, [] as string[]);

      // Filter out bounding box segments
      const filteredSegments = segments.filter((segment) => !isBoundingBoxSegment(segment));

      return filteredSegments.join(' ').trim();
    };

    /**
     * Check if a single path segment is a bounding box rectangle:
     * 1. Starts with M/m 0,0 (case insensitive)
     * 2. Contains width and height values that match SVG dimensions (within tolerance)
     */
    const isBoundingBoxSegment = (pathSegment: string): boolean => {
      if (!pathSegment) return false;

      const trimmedSegment = pathSegment.trim();

      // Check if segment starts with M/m 0,0 (with various spacing/comma formats)
      const startsWithM00 = /^[Mm]\s*0[,\s]+0/i.test(trimmedSegment);
      if (!startsWithM00) return false;

      // Extract all numbers from the segment and check if width and height values are present
      const numbers = trimmedSegment.match(/([-+]?\d*\.?\d+)/g)?.map(Number) || [];
      if (numbers.length < 4) return false; // Need at least 4 numbers for a rectangle

      // Check if any numbers match the SVG width and height (within tolerance)
      const hasWidth = numbers.some((num) => Math.abs(num - svgWidth) <= tolerance);
      const hasHeight = numbers.some((num) => Math.abs(num - svgHeight) <= tolerance);

      return hasWidth && hasHeight;
    };

    /**
     * Recursively process paths and remove bounding box segments from compound paths
     */
    const removeBoundingBoxRecursively = (node: INode): INode | null => {
      // Process path nodes
      if (node.name === 'path' && node.attributes?.d) {
        const cleanedPath = removeBoundingBoxFromCompoundPath(node.attributes.d);

        // If the cleaned path is empty, remove the entire path node
        if (!cleanedPath.trim()) {
          return null;
        }

        // If the path was modified, update the d attribute
        if (cleanedPath !== node.attributes.d) {
          return {
            ...node,
            attributes: {
              ...node.attributes,
              d: cleanedPath,
            },
          };
        }
      }

      // Process children recursively
      if (node.children && node.children.length > 0) {
        const filteredChildren = node.children.map(removeBoundingBoxRecursively).filter(Boolean) as INode[];

        return { ...node, children: filteredChildren };
      }

      return node;
    };

    // Apply the removal function to the SVG tree
    const cleanedSvgJson = removeBoundingBoxRecursively(svgJson);

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(cleanedSvgJson || svgJson);
    } else {
      return cleanedSvgJson || svgJson;
    }
  }

  /**
   * Remove margin from SVG by translating all elements and adjusting SVG dimensions
   * @param svgStringOrJson - The SVG string or INode to process
   * @param marginPx - The margin size in pixels to remove (default: 2)
   * @param precision - Coordinate calculation precision (default: 4)
   * @returns Promise<string | INode> - SVG with margin removed
   */
  static async removeMarginFromSvg(svgString: string, marginPx?: number): Promise<string>;

  static async removeMarginFromSvg(svgJson: INode, marginPx?: number): Promise<INode>;

  static async removeMarginFromSvg(
    svgStringOrJson: string | INode,
    marginPx = CUTLINER_OPTS.DEFAULT_IMG_RESIZE_MARGIN,
  ): Promise<string | INode> {
    if (marginPx <= 0) {
      return svgStringOrJson;
    }

    let svgJson: INode;
    let originalWidth: number;
    let originalHeight: number;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
      const size = this.extractSvgSize(svgStringOrJson);
      originalWidth = size.width;
      originalHeight = size.height;
    } else {
      svgJson = svgStringOrJson;
      const widthAttr = svgJson.attributes?.width;
      const heightAttr = svgJson.attributes?.height;

      if (!widthAttr || !heightAttr) {
        throw new Error('SVG must have width and height attributes for margin removal');
      }

      originalWidth = parseFloat(widthAttr);
      originalHeight = parseFloat(heightAttr);

      if (isNaN(originalWidth) || isNaN(originalHeight) || originalWidth <= 0 || originalHeight <= 0) {
        throw new Error('Invalid SVG width or height attributes');
      }
    }

    // Calculate new dimensions after margin removal
    const newWidth = originalWidth - 2 * marginPx;
    const newHeight = originalHeight - 2 * marginPx;

    if (newWidth <= 0 || newHeight <= 0) {
      throw new Error(
        `Cannot remove margin ${marginPx}px from SVG (${originalWidth}x${originalHeight}): resulting size would be non-positive`,
      );
    }

    // Apply global translate transform to remove margin
    const globalTransform = [1, 0, 0, 1, -marginPx, -marginPx]; // translate(-marginPx, -marginPx)

    // Use flattenTransformAttributes with global transform
    const transformedSvgJson = await this.flattenTransformAttributes(svgJson, 4, globalTransform);

    // Update SVG dimensions and viewBox
    const finalSvgJson: INode = {
      ...transformedSvgJson,
      attributes: {
        ...transformedSvgJson.attributes,
        width: newWidth.toString(),
        height: newHeight.toString(),
        viewBox: `0 0 ${newWidth} ${newHeight}`,
      },
    };

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(finalSvgJson);
    } else {
      return finalSvgJson;
    }
  }

  /**
   * SVG 요소들의 transform 속성을 모두 찾아서 직접 좌표값에 적용하고 transform 속성을 제거합니다.
   * 성능 최적화: translate(0,0), scale(1), rotate(0) 등 의미없는 transform은 건너뛰고,
   * 단위 행렬인 경우 좌표 변환을 생략합니다.
   * @param svgString - 원본 SVG 문자열
   * @param precision - 좌표 계산 시 소수점 정확도 (기본값: 4)
   * @param globalTransform - 전역적으로 적용할 transform 매트릭스 [a, b, c, d, e, f] (선택사항)
   * @returns Promise<string> - transform이 플래튼된 SVG 문자열
   */
  static async flattenTransformAttributes(
    svgString: string,
    precision?: number,
    globalTransform?: number[],
  ): Promise<string>;

  static async flattenTransformAttributes(
    svgJson: INode,
    precision?: number,
    globalTransform?: number[],
  ): Promise<INode>;

  static async flattenTransformAttributes(
    svgStringOrJson: string | INode,
    precision = 4,
    globalTransform?: number[],
  ): Promise<string | INode> {
    let svgJson: INode;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
    } else {
      svgJson = svgStringOrJson;
    }

    // Transform 파싱 및 매트릭스 변환 유틸리티 함수들
    const parseTransformValue = (transformStr: string): Array<{ type: string; values: number[] }> => {
      const transforms: Array<{ type: string; values: number[] }> = [];
      const transformRegex = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(\s*([^)]+)\s*\)/g;

      let match;
      while ((match = transformRegex.exec(transformStr)) !== null) {
        const type = match[1];
        const valuesStr = match[2];
        if (type && valuesStr) {
          const values = valuesStr
            .split(/[\s,]+/)
            .map(Number)
            .filter((n) => !isNaN(n));

          // 의미없는 transform 건너뛰기
          if (isNeutralTransform(type, values)) {
            continue;
          }

          transforms.push({ type, values });
        }
      }

      return transforms;
    };

    // 의미없는 transform인지 확인하는 함수
    const isNeutralTransform = (type: string, values: number[]): boolean => {
      const EPSILON = 1e-10; // 부동소수점 오차 허용

      switch (type) {
        case 'translate':
          // translate(0) 또는 translate(0,0)
          return (
            values.length === 0 ||
            (values.length >= 1 &&
              Math.abs(values[0] ?? 0) < EPSILON &&
              (values.length === 1 || Math.abs(values[1] ?? 0) < EPSILON))
          );

        case 'scale':
          // scale(1) 또는 scale(1,1)
          return (
            values.length === 0 ||
            (values.length >= 1 &&
              Math.abs((values[0] ?? 1) - 1) < EPSILON &&
              (values.length === 1 || Math.abs((values[1] ?? values[0] ?? 1) - 1) < EPSILON))
          );

        case 'rotate':
          // rotate(0) 또는 rotate(0,cx,cy)
          return values.length === 0 || Math.abs(values[0] ?? 0) < EPSILON;

        case 'skewX':
        case 'skewY':
          // skewX(0) 또는 skewY(0)
          return values.length === 0 || Math.abs(values[0] ?? 0) < EPSILON;

        case 'matrix':
          // matrix(1,0,0,1,0,0) - 단위 행렬
          if (values.length < 6) return false;
          return (
            Math.abs((values[0] ?? 1) - 1) < EPSILON &&
            Math.abs(values[1] ?? 0) < EPSILON &&
            Math.abs(values[2] ?? 0) < EPSILON &&
            Math.abs((values[3] ?? 1) - 1) < EPSILON &&
            Math.abs(values[4] ?? 0) < EPSILON &&
            Math.abs(values[5] ?? 0) < EPSILON
          );

        default:
          return false;
      }
    };

    // 매트릭스가 단위 행렬인지 확인하는 함수
    const isIdentityMatrix = (matrix: number[]): boolean => {
      const EPSILON = 1e-10;
      const identity = [1, 0, 0, 1, 0, 0];
      return matrix.every((val, idx) => Math.abs(val - (identity[idx] ?? 0)) < EPSILON);
    };

    const transformToMatrix = (transforms: Array<{ type: string; values: number[] }>): number[] => {
      // 빈 배열이면 단위 행렬 반환
      if (transforms.length === 0) {
        return [1, 0, 0, 1, 0, 0];
      }

      let matrix = [1, 0, 0, 1, 0, 0]; // 단위 행렬 [a, b, c, d, e, f]

      for (const { type, values } of transforms) {
        let newMatrix: number[];

        switch (type) {
          case 'matrix': {
            newMatrix = values.length >= 6 ? values.slice(0, 6) : [1, 0, 0, 1, 0, 0];
            break;
          }
          case 'translate': {
            const tx = values[0] || 0;
            const ty = values[1] || 0;
            newMatrix = [1, 0, 0, 1, tx, ty];
            break;
          }
          case 'scale': {
            const sx = values[0] || 1;
            const sy = values[1] || sx;
            newMatrix = [sx, 0, 0, sy, 0, 0];
            break;
          }
          case 'rotate': {
            const angle = ((values[0] || 0) * Math.PI) / 180;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const cx = values[1] || 0;
            const cy = values[2] || 0;
            if (cx !== 0 || cy !== 0) {
              newMatrix = [cos, sin, -sin, cos, -cx * cos + cy * sin + cx, -cx * sin - cy * cos + cy];
            } else {
              newMatrix = [cos, sin, -sin, cos, 0, 0];
            }
            break;
          }
          case 'skewX': {
            const skewAngleX = ((values[0] || 0) * Math.PI) / 180;
            newMatrix = [1, 0, Math.tan(skewAngleX), 1, 0, 0];
            break;
          }
          case 'skewY': {
            const skewAngleY = ((values[0] || 0) * Math.PI) / 180;
            newMatrix = [1, Math.tan(skewAngleY), 0, 1, 0, 0];
            break;
          }
          default:
            newMatrix = [1, 0, 0, 1, 0, 0];
        }

        // 매트릭스 곱셈: matrix = matrix * newMatrix
        const [a1, b1, c1, d1, e1, f1] = matrix;
        const [a2, b2, c2, d2, e2, f2] = newMatrix;

        matrix = [
          (a1 ?? 0) * (a2 ?? 0) + (c1 ?? 0) * (b2 ?? 0),
          (b1 ?? 0) * (a2 ?? 0) + (d1 ?? 0) * (b2 ?? 0),
          (a1 ?? 0) * (c2 ?? 0) + (c1 ?? 0) * (d2 ?? 0),
          (b1 ?? 0) * (c2 ?? 0) + (d1 ?? 0) * (d2 ?? 0),
          (a1 ?? 0) * (e2 ?? 0) + (c1 ?? 0) * (f2 ?? 0) + (e1 ?? 0),
          (b1 ?? 0) * (e2 ?? 0) + (d1 ?? 0) * (f2 ?? 0) + (f1 ?? 0),
        ];
      }

      return matrix;
    };

    const applyMatrixToCoordinates = (matrix: number[], x: number, y: number): [number, number] => {
      const [a, b, c, d, e, f] = matrix;
      const newX = (a ?? 0) * x + (c ?? 0) * y + (e ?? 0);
      const newY = (b ?? 0) * x + (d ?? 0) * y + (f ?? 0);
      return [newX, newY];
    };

    const roundToPrecision = (value: number): number => {
      return Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
    };

    // 재귀적으로 노드를 변환하는 함수
    const transformNode = (node: INode, parentMatrix: number[] = [1, 0, 0, 1, 0, 0]): INode => {
      const attrs = { ...(node.attributes || {}) };

      // 현재 노드의 transform 속성 처리
      let currentMatrix = [...parentMatrix];

      if (attrs.transform) {
        const transforms = parseTransformValue(attrs.transform);

        // 의미있는 transform이 없으면 속성만 제거하고, parentMatrix가 단위행렬이 아니면 좌표 변환 진행
        if (transforms.length === 0) {
          delete attrs.transform;
          // parentMatrix가 단위 행렬이면 조기 반환, 아니면 좌표 변환 진행
          if (isIdentityMatrix(parentMatrix)) {
            const transformedChildren =
              node.children?.map((child) => transformNode(child, parentMatrix)) ?? [];
            return { ...node, attributes: attrs, children: transformedChildren };
          }
          // parentMatrix가 단위 행렬이 아니므로 좌표 변환 진행을 위해 continue
        }

        if (transforms.length > 0) {
          const nodeMatrix = transformToMatrix(transforms);

          // 노드 매트릭스가 단위 행렬이면 변환 불필요
          if (isIdentityMatrix(nodeMatrix)) {
            delete attrs.transform;
            // 자식 노드들을 부모 매트릭스로 변환 (변환 없음)
            const transformedChildren =
              node.children?.map((child) => transformNode(child, parentMatrix)) ?? [];
            return { ...node, attributes: attrs, children: transformedChildren };
          }

          // 부모 매트릭스와 현재 매트릭스를 곱함
          const [a1, b1, c1, d1, e1, f1] = currentMatrix;
          const [a2, b2, c2, d2, e2, f2] = nodeMatrix;

          currentMatrix = [
            (a1 ?? 0) * (a2 ?? 0) + (c1 ?? 0) * (b2 ?? 0),
            (b1 ?? 0) * (a2 ?? 0) + (d1 ?? 0) * (b2 ?? 0),
            (a1 ?? 0) * (c2 ?? 0) + (c1 ?? 0) * (d2 ?? 0),
            (b1 ?? 0) * (c2 ?? 0) + (d1 ?? 0) * (d2 ?? 0),
            (a1 ?? 0) * (e2 ?? 0) + (c1 ?? 0) * (f2 ?? 0) + (e1 ?? 0),
            (b1 ?? 0) * (e2 ?? 0) + (d1 ?? 0) * (f2 ?? 0) + (f1 ?? 0),
          ];

          // transform 속성 제거
          delete attrs.transform;
        }
      }

      // 현재 매트릭스가 단위 행렬이면 좌표 변환 불필요
      if (isIdentityMatrix(currentMatrix)) {
        const transformedChildren =
          node.children?.map((child) => transformNode(child, [1, 0, 0, 1, 0, 0])) ?? [];
        return { ...node, attributes: attrs, children: transformedChildren };
      }

      // 좌표 속성을 현재 매트릭스로 변환
      const name = node.name;

      switch (name) {
        case 'path':
          if (attrs.d && typeof attrs.d === 'string') {
            const [a, b, c, d, e, f] = currentMatrix;
            const transformedPath = svgpath(attrs.d).matrix([a ?? 0, b ?? 0, c ?? 0, d ?? 0, e ?? 0, f ?? 0]);
            let pathData = transformedPath.toString();

            pathData = pathData.replace(/([-+]?\d*\.?\d+)/g, (match) => {
              const num = parseFloat(match);
              if (!isNaN(num)) {
                return roundToPrecision(num).toString();
              }
              return match;
            });

            attrs.d = pathData;
          }
          break;
        case 'rect':
          if (attrs.x !== undefined && attrs.y !== undefined) {
            const x = parseFloat(attrs.x) || 0;
            const y = parseFloat(attrs.y) || 0;
            const width = parseFloat(attrs.width || '0');
            const height = parseFloat(attrs.height || '0');

            // rect의 네 모서리를 변환
            const [x1, y1] = applyMatrixToCoordinates(currentMatrix, x, y);
            const [x2, y2] = applyMatrixToCoordinates(currentMatrix, x + width, y);
            const [x3, y3] = applyMatrixToCoordinates(currentMatrix, x + width, y + height);
            const [x4, y4] = applyMatrixToCoordinates(currentMatrix, x, y + height);

            // 단순한 변환(회전/기울임 없음)인 경우 rect 유지, 아니면 path로 변환
            const isSimpleTransform = (currentMatrix[1] ?? 0) === 0 && (currentMatrix[2] ?? 0) === 0;

            if (isSimpleTransform) {
              const newX = roundToPrecision(Math.min(x1, x2, x3, x4));
              const newY = roundToPrecision(Math.min(y1, y2, y3, y4));
              const newWidth = roundToPrecision(Math.abs(x2 - x1));
              const newHeight = roundToPrecision(Math.abs(y3 - y2));

              attrs.x = newX.toString();
              attrs.y = newY.toString();
              attrs.width = newWidth.toString();
              attrs.height = newHeight.toString();
            } else {
              // path로 변환
              const pathData =
                `M${roundToPrecision(x1)},${roundToPrecision(y1)} ` +
                `L${roundToPrecision(x2)},${roundToPrecision(y2)} ` +
                `L${roundToPrecision(x3)},${roundToPrecision(y3)} ` +
                `L${roundToPrecision(x4)},${roundToPrecision(y4)} Z`;

              return {
                ...node,
                name: 'path',
                attributes: { ...attrs, d: pathData },
                children: node.children?.map((child) => transformNode(child, [1, 0, 0, 1, 0, 0])) ?? [],
              };
            }
          }
          break;
        case 'circle':
          if (attrs.cx !== undefined && attrs.cy !== undefined) {
            const cx = parseFloat(attrs.cx) || 0;
            const cy = parseFloat(attrs.cy) || 0;
            const r = parseFloat(attrs.r || '0');

            const [newCx, newCy] = applyMatrixToCoordinates(currentMatrix, cx, cy);
            const scaleX = Math.sqrt(
              (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
            );
            const scaleY = Math.sqrt(
              (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
            );
            const avgScale = (scaleX + scaleY) / 2;

            attrs.cx = roundToPrecision(newCx).toString();
            attrs.cy = roundToPrecision(newCy).toString();
            attrs.r = roundToPrecision(r * avgScale).toString();
          }
          break;
        case 'ellipse':
          if (attrs.cx !== undefined && attrs.cy !== undefined) {
            const cx = parseFloat(attrs.cx) || 0;
            const cy = parseFloat(attrs.cy) || 0;
            const rx = parseFloat(attrs.rx || '0');
            const ry = parseFloat(attrs.ry || '0');

            const [newCx, newCy] = applyMatrixToCoordinates(currentMatrix, cx, cy);
            const scaleX = Math.sqrt(
              (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
            );
            const scaleY = Math.sqrt(
              (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
            );

            attrs.cx = roundToPrecision(newCx).toString();
            attrs.cy = roundToPrecision(newCy).toString();
            attrs.rx = roundToPrecision(rx * scaleX).toString();
            attrs.ry = roundToPrecision(ry * scaleY).toString();
          }
          break;
        case 'line':
          if (
            attrs.x1 !== undefined &&
            attrs.y1 !== undefined &&
            attrs.x2 !== undefined &&
            attrs.y2 !== undefined
          ) {
            const x1 = parseFloat(attrs.x1) || 0;
            const y1 = parseFloat(attrs.y1) || 0;
            const x2 = parseFloat(attrs.x2) || 0;
            const y2 = parseFloat(attrs.y2) || 0;

            const [newX1, newY1] = applyMatrixToCoordinates(currentMatrix, x1, y1);
            const [newX2, newY2] = applyMatrixToCoordinates(currentMatrix, x2, y2);

            attrs.x1 = roundToPrecision(newX1).toString();
            attrs.y1 = roundToPrecision(newY1).toString();
            attrs.x2 = roundToPrecision(newX2).toString();
            attrs.y2 = roundToPrecision(newY2).toString();
          }
          break;
        case 'polyline':
        case 'polygon':
          if (attrs.points) {
            const points = attrs.points
              .trim()
              .split(/\s+/)
              .map((pt) => {
                const coords = pt.split(',');
                if (coords.length === 2) {
                  const x = parseFloat(coords[0]!);
                  const y = parseFloat(coords[1]!);
                  if (!isNaN(x) && !isNaN(y)) {
                    const [newX, newY] = applyMatrixToCoordinates(currentMatrix, x, y);
                    return `${roundToPrecision(newX)},${roundToPrecision(newY)}`;
                  }
                }
                return pt;
              });
            attrs.points = points.join(' ');
          }
          break;
        case 'text':
          if (attrs.x !== undefined && attrs.y !== undefined) {
            const x = parseFloat(attrs.x) || 0;
            const y = parseFloat(attrs.y) || 0;

            const [newX, newY] = applyMatrixToCoordinates(currentMatrix, x, y);
            attrs.x = roundToPrecision(newX).toString();
            attrs.y = roundToPrecision(newY).toString();

            if (attrs['font-size']) {
              const fontSize = parseFloat(attrs['font-size']);
              if (!isNaN(fontSize)) {
                const scaleX = Math.sqrt(
                  (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                    (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
                );
                const scaleY = Math.sqrt(
                  (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                    (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
                );
                const avgScale = (scaleX + scaleY) / 2;
                attrs['font-size'] = roundToPrecision(fontSize * avgScale).toString();
              }
            }
          }
          break;
        case 'image':
          if (attrs.x !== undefined && attrs.y !== undefined) {
            const x = parseFloat(attrs.x) || 0;
            const y = parseFloat(attrs.y) || 0;
            const width = parseFloat(attrs.width || '0');
            const height = parseFloat(attrs.height || '0');

            const [newX, newY] = applyMatrixToCoordinates(currentMatrix, x, y);
            const scaleX = Math.sqrt(
              (currentMatrix[0] ?? 0) * (currentMatrix[0] ?? 0) +
                (currentMatrix[1] ?? 0) * (currentMatrix[1] ?? 0),
            );
            const scaleY = Math.sqrt(
              (currentMatrix[2] ?? 0) * (currentMatrix[2] ?? 0) +
                (currentMatrix[3] ?? 0) * (currentMatrix[3] ?? 0),
            );

            attrs.x = roundToPrecision(newX).toString();
            attrs.y = roundToPrecision(newY).toString();
            attrs.width = roundToPrecision(width * scaleX).toString();
            attrs.height = roundToPrecision(height * scaleY).toString();
          }
          break;
      }

      // 자식 노드들을 현재 매트릭스로 변환 (단위 행렬이 아닌 경우에만)
      const transformedChildren =
        node.children?.map((child) =>
          transformNode(child, isIdentityMatrix(currentMatrix) ? [1, 0, 0, 1, 0, 0] : currentMatrix),
        ) ?? [];

      return { ...node, attributes: attrs, children: transformedChildren };
    };

    // Apply global transform if provided
    const initialMatrix =
      globalTransform && globalTransform.length === 6 ? globalTransform : [1, 0, 0, 1, 0, 0];

    // 전체 SVG 변환
    const transformedSvg = transformNode(svgJson, initialMatrix);

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(transformedSvg);
    } else {
      return transformedSvg;
    }
  }

  /**
   * Normalize SVG viewBox when missing by using SVG's width and height attributes
   */
  static async normalizeViewBox(svgString: string): Promise<string>;
  /**
   * Normalize SVG viewBox when missing by using SVG's width and height attributes
   */
  static async normalizeViewBox(svgJson: INode): Promise<INode>;
  static async normalizeViewBox(svgStringOrJson: string | INode): Promise<string | INode> {
    let svgJson: INode;
    let svgWidth: number;
    let svgHeight: number;

    if (typeof svgStringOrJson === 'string') {
      svgJson = await parse(svgStringOrJson);
      const size = this.extractSvgSize(svgStringOrJson);
      svgWidth = size.width;
      svgHeight = size.height;
    } else {
      svgJson = svgStringOrJson;
      // INode에서 width/height 추출
      const widthAttr = svgJson.attributes?.width;
      const heightAttr = svgJson.attributes?.height;

      if (!widthAttr || !heightAttr) {
        throw new Error('SVG must have width and height attributes for viewBox normalization');
      }

      svgWidth = parseFloat(widthAttr);
      svgHeight = parseFloat(heightAttr);

      if (isNaN(svgWidth) || isNaN(svgHeight) || svgWidth <= 0 || svgHeight <= 0) {
        throw new Error('Invalid SVG width or height attributes');
      }
    }

    // viewBox가 이미 존재하는지 확인
    const currentViewBox = svgJson.attributes?.viewBox;
    if (currentViewBox && typeof currentViewBox === 'string' && currentViewBox.trim()) {
      // viewBox가 이미 존재하면 그대로 반환
      if (typeof svgStringOrJson === 'string') {
        return stringify(svgJson);
      } else {
        return svgJson;
      }
    }

    // viewBox가 없으면 width, height를 기반으로 생성
    const normalizedSvgJson: INode = {
      ...svgJson,
      attributes: {
        ...svgJson.attributes,
        viewBox: `0 0 ${svgWidth} ${svgHeight}`,
      },
    };

    // Return the appropriate type based on input
    if (typeof svgStringOrJson === 'string') {
      return stringify(normalizedSvgJson);
    } else {
      return normalizedSvgJson;
    }
  }

  /**
   * SVG 문자열에서 href로 링크된 image 요소들을 찾아서 base64 embed 형태로 변환합니다.
   * 이미지의 렌더링 위치, 크기 정보는 기존과 동일하게 유지됩니다.
   * @param svgString - 원본 SVG 문자열
   * @returns Promise<string> - href 링크가 base64 embed로 변환된 SVG 문자열
   */
  static async convertImageLinkToEmbedInSvg(svgString: string): Promise<string> {
    const svgJson = await parse(svgString);

    // 재귀적으로 image 요소를 찾아서 href를 base64로 변환하는 함수
    const convertImageNodes = async (node: INode): Promise<INode> => {
      // image 요소인 경우 href 속성을 확인하고 변환
      if (node.name === 'image' && node.attributes?.href) {
        const href = node.attributes.href;

        // 이미 data URI인 경우 그대로 반환
        if (href.startsWith('data:')) {
          return node;
        }

        try {
          // URL을 base64 datauri로 변환
          const dataUri = await this.urlToDataUri(href);

          // 새로운 node 생성 (기존 속성은 모두 유지하고 href만 변경)
          return {
            ...node,
            attributes: {
              ...node.attributes,
              href: dataUri,
            },
          };
        } catch (error) {
          // 변환 실패 시 원본 그대로 반환하고 경고 출력
          console.warn(`Failed to convert image URL to base64: ${href}`, error);
          return node;
        }
      }

      // 자식 노드들도 재귀적으로 처리
      if (node.children && node.children.length > 0) {
        const convertedChildren = await Promise.all(node.children.map((child) => convertImageNodes(child)));

        return {
          ...node,
          children: convertedChildren,
        };
      }

      // image 요소가 아니거나 자식이 없는 경우 그대로 반환
      return node;
    };

    // SVG 전체를 변환
    const convertedSvgJson = await convertImageNodes(svgJson);

    // 변환된 SVG JSON을 문자열로 변환하여 반환
    return stringify(convertedSvgJson);
  }

  /**
   * SVG의 viewBox margin을 확장합니다.
   * @param svgString - 원본 SVG 문자열
   * @param marginPx - 확장할 margin 크기(픽셀)
   * @returns string - viewBox margin이 확장된 SVG 문자열
   */
  static expandViewBoxMargin(
    svgString: string,
    marginPx: number,
    postProcessFn?: (svg: string) => string,
  ): string {
    if (marginPx <= 0) {
      return svgString;
    }

    // SVG width, height 추출
    const svgTagMatch = svgString.match(/<svg[^>]*>/i);
    if (!svgTagMatch) throw new Error('No <svg> tag found');

    const svgTag = svgTagMatch[0];
    const widthMatch = svgTag.match(/width\s*=\s*['"]([\d.]+)['"]/i);
    const heightMatch = svgTag.match(/height\s*=\s*['"]([\d.]+)['"]/i);

    if (!widthMatch || !heightMatch || !widthMatch[1] || !heightMatch[1]) {
      throw new Error('SVG must have width and height attributes');
    }

    const svgWidth = parseFloat(widthMatch[1]);
    const svgHeight = parseFloat(heightMatch[1]);

    // viewBox 추출 및 파싱
    const viewBoxMatch = svgTag.match(/viewBox\s*=\s*['"]([\d.\s-]+)['"]/i);
    let vbX = 0;
    let vbY = 0;
    let vbWidth = svgWidth;
    let vbHeight = svgHeight;

    if (viewBoxMatch && viewBoxMatch[1]) {
      const parts = viewBoxMatch[1].split(/\s+/).map(Number);
      if (parts.length === 4 && parts.every((v) => !isNaN(v))) {
        vbX = parts[0] ?? 0;
        vbY = parts[1] ?? 0;
        vbWidth = parts[2] ?? svgWidth;
        vbHeight = parts[3] ?? svgHeight;
      }
    }

    // viewBox margin 확장
    const newVbX = vbX - marginPx;
    const newVbY = vbY - marginPx;
    const newVbWidth = vbWidth + 2 * marginPx;
    const newVbHeight = vbHeight + 2 * marginPx;
    const newViewBox = `${newVbX} ${newVbY} ${newVbWidth} ${newVbHeight}`;

    // viewBox 업데이트
    let newSvgTag: string;
    if (viewBoxMatch) {
      // 기존 viewBox 교체
      newSvgTag = svgTag.replace(/viewBox\s*=\s*['"][^'"]*['"]/i, `viewBox="${newViewBox}"`);
    } else {
      // viewBox 추가
      newSvgTag = svgTag.replace(/<svg/i, `<svg viewBox="${newViewBox}"`);
    }

    const expandedSvg = svgString.replace(svgTag, newSvgTag);

    return postProcessFn ? postProcessFn(expandedSvg) : expandedSvg;
  }

  static injectDropShadow(svg: string): string {
    const { dx, dy, stdDeviation, color, opacity, filterId } = CUTLINER_OPTS.DEFAULT_SHADOW;

    // const blurId = `${filterId}-blur`;
    // const offsetId = `${filterId}-offset`;
    // const floodId = `${filterId}-flood`;

    const filterDef =
      `<filter id="${filterId}" x="-50%" y="-50%" width="200%" height="200%" filterUnits="objectBoundingBox">` +
      `<feGaussianBlur in="SourceAlpha" stdDeviation="${stdDeviation}" result="blur"/>` +
      `<feOffset in="blur" dx="${dx}" dy="${dy}" result="offsetBlur"/>` +
      `<feFlood flood-color="${color}" flood-opacity="${opacity}" result="color"/>` +
      `<feComposite in="color" in2="offsetBlur" operator="in" result="shadow"/>` +
      `<feMerge>` +
      `<feMergeNode in="shadow"/>` +
      `<feMergeNode in="SourceGraphic"/>` +
      `</feMerge>` +
      `</filter>`;

    if (/<defs>/.test(svg)) {
      svg = svg.replace(/<defs>([\s\S]*?)<\/defs>/, `<defs>$1${filterDef}</defs>`);
    } else {
      svg = svg.replace(/<svg([^>]*)>/, `<svg$1><defs>${filterDef}</defs>`);
    }

    //  기존 stroke 관련 속성 제거
    svg = svg.replace(/\sstroke-dasharray="[^"]*"/g, '');
    svg = svg.replace(/stroke-dasharray:\s*[^;"']+;?/g, '');
    svg = svg.replace(/\sstroke="[^"]*"/g, '');
    svg = svg.replace(/stroke:\s*[^;"']+;?/g, '');

    svg = svg.replace(/<path\b/g, `<path filter="url(#${filterId})"`);

    return svg;
  }

  /**
   * 재질 이미지를 원본(use#design-image-svg)과 feBlend로 섞기
   */

  static async composeStickerWithMaterial(
    svgString: string,
    materialInfo?: { source: string; type: string },
  ) {
    if (UtilS.isEmpty(materialInfo) || !materialInfo?.source) return svgString;

    const SVG_NS = 'http://www.w3.org/2000/svg';
    const XLINK_NS = 'http://www.w3.org/1999/xlink';

    const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    const svg = doc.documentElement;

    const vb = svg.getAttribute('viewBox');
    if (!vb) return svgString;
    const [vx, vy, vw, vh] = vb.split(/\s+/).map(Number);

    const cutPath = svg.querySelector('path[stroke-dasharray]') || svg.querySelector('path');
    if (!cutPath) return svgString;

    // defs
    let defs = svg.querySelector('defs');
    if (!defs) {
      defs = doc.createElementNS(SVG_NS, 'defs');
      svg.insertBefore(defs, svg.firstChild);
    }
    if (!svg.getAttribute('xmlns:xlink')) svg.setAttribute('xmlns:xlink', XLINK_NS);

    const dataUri = await this.urlToDataUri(materialInfo.source);

    const uid = Date.now().toString(36);
    const cutId = `cut-${uid}`;
    const clipId = `clip-${uid}`;
    const filId = `matfx-${uid}`;

    // 컷라인 재사용
    const cutDef = doc.createElementNS(SVG_NS, 'path');
    cutDef.setAttribute('id', cutId);
    cutDef.setAttribute('d', cutPath.getAttribute('d') || '');
    defs.appendChild(cutDef);

    const clip = doc.createElementNS(SVG_NS, 'clipPath');
    clip.setAttribute('id', clipId);
    const useForClip = doc.createElementNS(SVG_NS, 'use');
    useForClip.setAttributeNS(XLINK_NS, 'xlink:href', `#${cutId}`);
    clip.appendChild(useForClip);
    defs.appendChild(clip);

    const filter = doc.createElementNS(SVG_NS, 'filter');
    filter.setAttribute('id', filId);
    filter.setAttribute('filterUnits', 'userSpaceOnUse');
    filter.setAttribute('x', String(vx));
    filter.setAttribute('y', String(vy));
    filter.setAttribute('width', String(vw));
    filter.setAttribute('height', String(vh));
    filter.setAttribute('style', 'color-interpolation-filters:sRGB');

    // 재질 입력
    const feImg = doc.createElementNS(SVG_NS, 'feImage');
    feImg.setAttribute('href', dataUri);
    feImg.setAttributeNS(XLINK_NS, 'xlink:href', dataUri);
    feImg.setAttribute('x', String(vx));
    feImg.setAttribute('y', String(vy));
    feImg.setAttribute('width', String(vw));
    feImg.setAttribute('height', String(vh));
    feImg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    feImg.setAttribute('result', 'mat');
    filter.appendChild(feImg);

    const feBlend = doc.createElementNS(SVG_NS, 'feBlend');
    feBlend.setAttribute('mode', materialInfo.type || 'overlay');
    feBlend.setAttribute('in', 'SourceGraphic'); // 필터 대상(원본 use)
    feBlend.setAttribute('in2', 'mat'); // 재질
    feBlend.setAttribute('result', 'blended');
    filter.appendChild(feBlend);

    if (materialInfo.type === 'overlay') {
      // 원본 알파만 유지(빈 영역은 투명)
      const feComp = doc.createElementNS(SVG_NS, 'feComposite');
      feComp.setAttribute('in', 'blended');
      feComp.setAttribute('in2', 'SourceAlpha');
      feComp.setAttribute('operator', 'in');
      feComp.setAttribute('result', 'final');
      filter.appendChild(feComp);
    }

    if (materialInfo.type === 'multiply') {
      // (C) 배경 재질 + 섞인 원본 합치기
      const feMerge = doc.createElementNS(SVG_NS, 'feMerge');
      const n1 = doc.createElementNS(SVG_NS, 'feMergeNode');
      n1.setAttribute('in', 'mat');
      const n2 = doc.createElementNS(SVG_NS, 'feMergeNode');
      n2.setAttribute('in', 'blended');
      feMerge.appendChild(n1);
      feMerge.appendChild(n2);
      feMerge.setAttribute('result', 'final');
      filter.appendChild(feMerge);
    }

    defs.appendChild(filter);

    const clipGroup = doc.createElementNS(SVG_NS, 'g');
    clipGroup.setAttribute('clip-path', `url(#${clipId})`);
    clipGroup.setAttribute('style', 'isolation:isolate');

    const origUse = svg.querySelector(
      'use[href="#design-image-svg"], use[xlink\\:href="#design-image-svg"]',
    ) as SVGUseElement | null;

    if (origUse) {
      const filtered = origUse.cloneNode(true) as SVGUseElement;
      filtered.setAttribute('filter', `url(#${filId})`);
      clipGroup.appendChild(filtered);
    }

    svg.appendChild(clipGroup);

    const outlineTop = doc.createElementNS(SVG_NS, 'use');
    outlineTop.setAttributeNS(XLINK_NS, 'xlink:href', `#${cutId}`);
    outlineTop.setAttribute('fill', 'none');
    [
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'stroke-miterlimit',
      'stroke-dasharray',
      'stroke-dashoffset',
    ].forEach((attr) => {
      const v = cutPath.getAttribute(attr);
      if (v != null) outlineTop.setAttribute(attr, v);
    });
    outlineTop.setAttribute('vector-effect', 'non-scaling-stroke');
    outlineTop.setAttribute('style', 'mix-blend-mode:normal');
    svg.appendChild(outlineTop);

    return new XMLSerializer().serializeToString(doc);
  }
}
